"use client"
import { apiSpecificOffers } from '@/apis/offers';
import { apiExpiredOffers } from '@/apis/user';
import { Offer, OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl, getFinalDiscountTag } from '@/constants/hooks';
import dynamic from 'next/dynamic';
import { apiOfferDetails } from '@/apis/offers';
import Spinner from 'react-bootstrap/Spinner';

const Banner = dynamic(() => import('./Banner'), { ssr: false });
const OffersListView = dynamic(() => import('./OffersListView'));
const OfferModal = dynamic(() => import('./OfferModal'), { ssr: false });
const SimpleOfferModal = dynamic(() => import('./SimpleOfferModal'), { ssr: false });

interface Props {
    initialOffers: OffersOffer[];
    companyId: string;
    merchantId: string;
    awaited_p_id: string;
    mer_slug_type: string;
    mer_slug: string;
    offerBanner: OffersOffer[];
    domain: string;
    merchantName: string;
    pagination: PaginationType;
    ads_campaign: boolean;
}

const LazyLoadingOffers = ({ initialOffers, companyId, merchantId, awaited_p_id, mer_slug_type, mer_slug, offerBanner, domain, merchantName, pagination, ads_campaign }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers);
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef<IntersectionObserver | null>(null);
    const [filteredOfferBanners, setFilteredOfferBanners] = useState<OffersOffer[]>([]);
    const bannerDisplayAfter = 2;

    // Single modal state — one object controls everything
    // null = closed, non-null = open with this data
    const [modalData, setModalData] = useState<Offer | null>(null);
    const [modalMerchantHref, setModalMerchantHref] = useState<string>('');
    const [modalFinalDiscountTag, setModalFinalDiscountTag] = useState<string | null>(null);
    const [modalMerchantImg, setModalMerchantImg] = useState<string>('');
    const [modalProductImg, setModalProductImg] = useState<string | null>(null);
    // Prevents double-click firing multiple fetches
    const [modalLoading, setModalLoading] = useState(false);
    const loadMoreRef = useRef<() => void>(() => { });

    // ✅ Reset everything when merchant changes (SPA navigation)
    useEffect(() => {
        setOffers(initialOffers);
        setPage(1);
        setHasMore(true);
    }, [initialOffers]);

    // ✅ Fetch expired offers only when merchant changes — not on every render
    const fetchExpiredOffers = useCallback(async () => {
        try {
            const response = await apiExpiredOffers(merchantId, companyId);
            setExpiredOffers(response.data?.offers || []);
        } catch (error) {
            // silently fail
        }
    }, [merchantId, companyId]);

    useEffect(() => {
        fetchExpiredOffers();
    }, [fetchExpiredOffers]);

    // ✅ Auto-open modal on page load only when URL has show=true
    // Guards against firing on every page load
    useEffect(() => {
        if (!awaited_p_id || !companyId) return;
        if (typeof window === 'undefined') return;

        const params = new URLSearchParams(window.location.search);
        if (params.get('show') !== 'true') return;

        const fetchInitialModal = async () => {
            try {
                const offer_details = await apiOfferDetails(awaited_p_id, companyId);
                if (!offer_details?.data) return;

                const matchingOffer = initialOffers.find(
                    o => o.offer?.unique_id === awaited_p_id
                );
                if (matchingOffer) {
                    const originalPrice = matchingOffer.offer?.original_price
                        ? parseFloat(matchingOffer.offer.original_price) : 0;
                    const salePrice = matchingOffer.offer?.sale_price
                        ? parseFloat(matchingOffer.offer.sale_price) : 0;
                    const discountPercent = originalPrice > 0 && salePrice > 0
                        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
                        : null;
                    setModalFinalDiscountTag(getFinalDiscountTag(
                        matchingOffer.offer?.offer_title || matchingOffer.offer?.offer_detail,
                        discountPercent
                    ));
                    setModalMerchantImg(matchingOffer.merchant?.merchant_logo || '');
                    setModalProductImg(matchingOffer.offer?.product_image || null);
                }
                setModalData(offer_details.data);
            } catch (error) {
                console.error("Error fetching offer details on load:", error);
            }
        };

        fetchInitialModal();
    }, [awaited_p_id, companyId]);

    // ✅ Called when user clicks any card's button
    // Uses offer.offer.unique_id — the CLICKED card's ID, not awaited_p_id
    // modalLoading guard prevents double-click firing multiple fetches
    const handleOfferClick = useCallback(async (
        offer: OffersOffer,
        merchantHref: string
    ) => {
        if (modalLoading) return; // guard against double-click
        setModalLoading(true);
        try {
            // ✅ offer.offer.unique_id — correct, uses clicked card's own ID
            const offer_details = await apiOfferDetails(offer.offer.unique_id, companyId);
            if (!offer_details?.data) return;

            const originalPrice = offer.offer?.original_price
                ? parseFloat(offer.offer.original_price) : 0;
            const salePrice = offer.offer?.sale_price
                ? parseFloat(offer.offer.sale_price) : 0;
            const discountPercent = originalPrice > 0 && salePrice > 0
                ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
                : null;

            setModalFinalDiscountTag(getFinalDiscountTag(
                offer.offer?.offer_title || offer.offer?.offer_detail,
                discountPercent
            ));
            setModalMerchantHref(merchantHref);
            setModalMerchantImg(offer.merchant?.merchant_logo || '');
            setModalProductImg(offer.offer?.product_image || null);
            setModalData(offer_details.data);
        } catch (error) {
            console.error("Error fetching offer details on click:", error);
        } finally {
            setModalLoading(false);
        }
    }, [modalLoading, companyId]);

    const handleCloseModal = useCallback(() => {
        setModalData(null);
        setModalMerchantHref('');
        setModalFinalDiscountTag(null);
        setModalMerchantImg('');
        setModalProductImg(null);
    }, []);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        if (page === pagination?.last_page) {
            setHasMore(false);
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const response = await apiSpecificOffers(merchantId, companyId, nextPage);
            if (response.data?.offers.length > 0) {
                setOffers(prev => [...prev, ...response.data.offers]);
                setPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading more offers:', error);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, page, pagination?.last_page, merchantId, companyId]);

    useEffect(() => {
        loadMoreRef.current = loadMore;
    }, [loadMore]);

    const sentinelRef = useCallback((node: HTMLDivElement | null) => {
        if (observer.current) observer.current.disconnect();
        if (!node || !hasMore) return;
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreRef.current(); // uses ref — no stale closure
            }
        }, { threshold: 0.1 });
        observer.current.observe(node);
    }, [hasMore]); // loadMore removed from deps

    useEffect(() => {
        if (offerBanner?.length > 0) {
            setFilteredOfferBanners(filterOfferBanners(offerBanner, 300, 600, 50, 150))
        }
    }, [offerBanner])

    return (
        <>
            {/* ONE modal — null = closed, data = open */}
            {modalData != null && !ads_campaign && (
                <OfferModal
                    data={modalData}
                    companyId={companyId}
                    onClose={handleCloseModal}
                    domain={domain}
                    merchantHref={modalMerchantHref}
                    merchantImg={modalMerchantImg}
                    productImg={modalProductImg}
                />
            )}
            {modalData != null && ads_campaign && (
                <SimpleOfferModal
                    data={modalData}
                    onClose={handleCloseModal}
                    domain={domain}
                    merchantHref={modalMerchantHref}
                    finalDiscountTag={modalFinalDiscountTag}
                    merchantImg={modalMerchantImg}
                />
            )}

            {/* Loading overlay for when user clicks a card and fetch is in progress */}
            {modalLoading && (
                <div
                    style={{
                        position: 'fixed', inset: 0,
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.3)',
                        zIndex: 9999
                    }}
                >
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading offer...</span>
                    </Spinner>
                </div>
            )}

            {/* Active Offers Section */}
            <section className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {offers.map((offer, index) => (
                        offer.status === 1 && offer.offer.status === 1 ? (
                            <React.Fragment key={offer.id}>
                                <div className="w-full">
                                    <OffersListView
                                        product={offer}
                                        companyId={companyId}
                                        awaited_p_id={awaited_p_id}
                                        mer_slug_type={mer_slug_type}
                                        mer_slug={mer_slug}
                                        domain={domain}
                                        ads_campaign={ads_campaign}
                                        onOfferClick={handleOfferClick}
                                    />
                                </div>
                                {(index + 1) % bannerDisplayAfter === 0 &&
                                    filteredOfferBanners.length > 0 &&
                                    filteredOfferBanners[Math.floor(index / bannerDisplayAfter)] && (() => {
                                        const banner = filteredOfferBanners[Math.floor(index / bannerDisplayAfter)];
                                        const dimension = getBannerDimensions(banner);
                                        return (
                                            <div className="col-span-1 sm:col-span-2 lg:col-span-3 w-full flex justify-center py-4" key={`banner-${Math.floor(index / bannerDisplayAfter)}`}>
                                                <Banner
                                                    data={banner}
                                                    height={dimension?.height}
                                                    width={dimension?.width}
                                                    offerLink={null}
                                                    domain={domain}
                                                    mer_slug={mer_slug}
                                                    slug_type={mer_slug_type}
                                                />
                                            </div>
                                        );
                                    })()
                                }
                            </React.Fragment>
                        ) : null
                    ))}

                    <div ref={sentinelRef} className="col-span-1 sm:col-span-2 lg:col-span-3" style={{ height: '1px' }} aria-hidden="true" />

                    {/* Loading Spinner */}
                    {loading && (
                        <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex justify-center items-center py-12">
                            <div className="relative w-12 h-12">
                                <div className="absolute inset-0 border-4 border-[#E5E7EB] rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-[#F97316] rounded-full animate-spin"></div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Expired Offers Section */}
            {expiredOffers?.length > 0 && (
                <section className="w-full pt-16 mt-16 border-t-2 border-[#E5E7EB]">
                    <div className="flex flex-col gap-8">
                        {/* Section Header */}
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFF7ED] to-[#FFEDD5] rounded-full mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-[#F97316]">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-black text-[#EA580C] uppercase tracking-wide">Worth Checking</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-black text-[#111827] mb-3">
                                Previously Active Deals
                            </h2>
                            <p className="text-base text-[#4B5563] max-w-2xl mx-auto">
                                Some expired coupons for <span className="font-bold text-[#F97316]">{merchantName}</span> might still work. Give them a try!
                            </p>
                        </div>

                        {/* Expired Offers Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {expiredOffers?.slice(0, 10).map((item, i) => (
                                <OffersListView
                                    key={item.id}
                                    product={item}
                                    companyId={companyId}
                                    awaited_p_id={awaited_p_id}
                                    mer_slug_type={mer_slug_type}
                                    mer_slug={mer_slug}
                                    domain={domain}
                                    ads_campaign={ads_campaign}
                                    onOfferClick={handleOfferClick}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Schema Markup */}
            <script
                type="application/ld+json"
                id='AggregateOffer'
                dangerouslySetInnerHTML={{
                    "__html": JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AggregateOffer",
                        "name": `${merchantName} Coupons & Deals`,
                        "description": `Currently, there are several active coupons and deals available for ${merchantName} products.`,
                        "offerCount": offers.length,
                        "offers": offers.map((offer) => ({
                            "@type": "Offer",
                            "name": offer?.offer?.offer_title,
                            "description": offer?.offer?.offer_detail,
                            "url": getBaseImageUrl(`${domain}`, `${offer.offer?.url}`, ""),
                        }))
                    })
                }}
            />
        </>
    )
}

export default LazyLoadingOffers
